import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useHybridAuth } from './useHybridAuth';
import { driverApi } from '../services/driverApi';
import { useDriverProfile } from './useDriverProfile';
import { companyIdFromProfileOrStorage } from './userCompanies.shared';

interface CreateImpoundParams {
  dispatchId: string;
  driverId?: string;
  locationId?: string;
  storageLot: string;
  status?: 'ACTIVE' | 'RELEASED';
}

export const useCreateImpound = () => {
  const { getAccessTokenSilently } = useHybridAuth();
  const queryClient = useQueryClient();
  const { data: driverProfile } = useDriverProfile();

  return useMutation({
    mutationFn: async (params: CreateImpoundParams) => {
      // An office member who does not drive has no Driver row, so the
      // profile's companyId is empty: the selected company is the one.
      const companyId = companyIdFromProfileOrStorage(driverProfile);
      if (!companyId) {
        throw new Error('Driver profile or company ID not available');
      }

      const token = await getAccessTokenSilently();
      driverApi.setAuthToken(token);

      return driverApi.createImpound({
        ...params,
        companyId,
      });
    },
    onSuccess: (data) => {
      // Invalidate and refetch impounds to get updated data
      queryClient.invalidateQueries({ queryKey: ['driver-impounds'] });
      // The new shell's Impounds list, so the impound just made shows.
      queryClient.invalidateQueries({ queryKey: ['mobile-impounds'] });

      // Also invalidate the specific impound query that ImpoundScreen might use
      queryClient.invalidateQueries({ queryKey: ['driver-impound', data.id] });

      // Invalidate calls as the call status might have changed
      queryClient.invalidateQueries({ queryKey: ['driver-calls'] });
    },
    onError: (error) => {
      console.error('Failed to create impound:', error);
    },
  });
};
